import React, { useEffect, useMemo, useState } from "react";
import { getInwardEntries, getSalesEntries } from "../storage";
import { formatDisplayDate } from "../utils/dateFormat";
import { getSettlementSummary } from "../utils/settlement";
import "./PurchaseSalesDashboard.css";

const moneyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

function formatMoney(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function dateKey(value) {
  return String(value || "").slice(0, 10);
}

function localToday() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function settlementFor(entry) {
  const summary = getSettlementSummary(entry?.total, entry?.payments);
  if ((entry?.paymentDone === true || Number(entry?.paymentDone) === 1) && summary.settledAmount === 0) {
    return { ...summary, paidAmount: summary.totalAmount, settledAmount: summary.totalAmount, pendingAmount: 0 };
  }
  return summary;
}

function totalsFor(entries) {
  return entries.reduce(
    (totals, entry) => {
      const settlement = settlementFor(entry);
      totals.amount += settlement.totalAmount;
      totals.paid += settlement.paidAmount;
      totals.pending += settlement.pendingAmount;
      totals.count += 1;
      return totals;
    },
    { amount: 0, paid: 0, pending: 0, count: 0 }
  );
}

function OverviewPanel({ title, totalLabel, totals, tone }) {
  return (
    <section className={`ps-overview-panel ps-overview-${tone}`}>
      <div className="ps-overview-heading">
        <span className="ps-overview-marker" aria-hidden="true" />
        <h2>{title}</h2>
      </div>
      <div className="ps-overview-total">
        <span>{totalLabel}</span>
        <strong>{formatMoney(totals.amount)}</strong>
      </div>
      <div className="ps-overview-details">
        <div><span>Paid</span><strong>{formatMoney(totals.paid)}</strong></div>
        <div><span>Pending</span><strong>{formatMoney(totals.pending)}</strong></div>
        <div><span>Entries</span><strong>{totals.count}</strong></div>
      </div>
    </section>
  );
}

function RecentTable({ title, rows, type }) {
  const isPurchase = type === "purchase";
  return (
    <section className="ps-recent-panel">
      <div className="ps-panel-heading">
        <h2>{title}</h2>
        <span>{rows.length} recent</span>
      </div>
      <div className="ps-table-wrap">
        <table className="ps-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>{isPurchase ? "Supplier" : "Customer"}</th>
              <th className="ps-number">Amount</th>
              <th className="ps-number">Pending</th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((entry, index) => {
              const settlement = settlementFor(entry);
              const party = isPurchase
                ? String(entry?.supplierName || "Unknown supplier")
                : String(entry?.customerName || entry?.customer || "Walk-in").split(/\r?\n/)[0];
              const entryDate = isPurchase ? entry?.createdAt : entry?.saleDate;
              return (
                <tr key={entry?.id || `${type}-${index}`}>
                  <td>{formatDisplayDate(entryDate)}</td>
                  <td>{party}</td>
                  <td className="ps-number">{formatMoney(settlement.totalAmount)}</td>
                  <td className="ps-number">{formatMoney(settlement.pendingAmount)}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={4} className="ps-empty">No {type} entries available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function PurchaseSalesDashboard() {
  const [inwardEntries, setInwardEntries] = useState([]);
  const [salesEntries, setSalesEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const [purchases, sales] = await Promise.all([getInwardEntries(), getSalesEntries()]);
      setInwardEntries(Array.isArray(purchases) ? purchases : []);
      setSalesEntries(Array.isArray(sales) ? sales : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purchases = useMemo(
    () => inwardEntries.filter((entry) => String(entry?.category || "General").trim() === "General"),
    [inwardEntries]
  );
  const sales = useMemo(
    () => salesEntries.filter((entry) => String(entry?.category || "").trim() === "Billing"),
    [salesEntries]
  );

  const today = localToday();
  const purchaseTotals = useMemo(() => totalsFor(purchases), [purchases]);
  const salesTotals = useMemo(() => totalsFor(sales), [sales]);
  const todayPurchaseTotals = useMemo(
    () => totalsFor(purchases.filter((entry) => dateKey(entry?.createdAt) === today)),
    [purchases, today]
  );
  const todaySalesTotals = useMemo(
    () => totalsFor(sales.filter((entry) => dateKey(entry?.saleDate) === today)),
    [sales, today]
  );
  const recentPurchases = useMemo(
    () => [...purchases].sort((a, b) => dateKey(b?.createdAt).localeCompare(dateKey(a?.createdAt))).slice(0, 5),
    [purchases]
  );
  const recentSales = useMemo(
    () => [...sales].sort((a, b) => dateKey(b?.saleDate).localeCompare(dateKey(a?.saleDate))).slice(0, 5),
    [sales]
  );

  return (
    <div className="ps-dashboard">
      <header className="ps-dashboard-header">
        <div>
          <span className="ps-dashboard-kicker">ARF SEAFOODS</span>
          <h1>Purchase &amp; Sales Dashboard</h1>
          <p>A focused view of material purchases and sales performance.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh Data"}
        </button>
      </header>

      {error ? <div className="ps-dashboard-error" role="alert">{error}</div> : null}

      <div className="ps-metric-grid" aria-label="Today's purchase and sales summary">
        <article className="ps-metric-card">
          <span>Today&apos;s Purchases</span>
          <strong>{formatMoney(todayPurchaseTotals.amount)}</strong>
          <small>{todayPurchaseTotals.count} entries</small>
        </article>
        <article className="ps-metric-card ps-metric-sales">
          <span>Today&apos;s Sales</span>
          <strong>{formatMoney(todaySalesTotals.amount)}</strong>
          <small>{todaySalesTotals.count} bills</small>
        </article>
        <article className="ps-metric-card">
          <span>Purchase Pending</span>
          <strong>{formatMoney(purchaseTotals.pending)}</strong>
          <small>Across all purchases</small>
        </article>
        <article className="ps-metric-card ps-metric-sales">
          <span>Sales Pending</span>
          <strong>{formatMoney(salesTotals.pending)}</strong>
          <small>Across all sales</small>
        </article>
      </div>

      <div className="ps-overview-grid">
        <OverviewPanel title="Purchase Overview" totalLabel="Total purchases" totals={purchaseTotals} tone="purchase" />
        <OverviewPanel title="Sales Overview" totalLabel="Total sales" totals={salesTotals} tone="sales" />
      </div>

      <div className="ps-recent-grid">
        <RecentTable title="Recent Purchases" rows={recentPurchases} type="purchase" />
        <RecentTable title="Recent Sales" rows={recentSales} type="sales" />
      </div>
    </div>
  );
}
